import { useCallback, useLayoutEffect, useRef, useState } from "react";
import {
	engagementTermsAPI,
	type CampaignEngagementTerms,
	type EngagementTermsCustomizationField,
} from "@/api/engagementTerms";
import {
	createEmptySnapshot,
	getCustomizedFields,
	snapshotFromResponse,
	snapshotToPayload,
	type CetSnapshot,
} from "./engagementTermsState";
import {
	getEngagementTermsCreatorKey,
	getEngagementTermsMarketKey,
	invalidateCachedCreatorTermsForMarket,
	loadCachedCreatorTerms,
	loadCachedMarketTerms,
	type EngagementTermsCache,
} from "./engagementTermsCache";

interface Options {
	campaignId: string | null;
	marketBlockId?: string;
	creatorAssignmentId?: string | null;
	onHydrate: (snapshot: CetSnapshot) => void;
	cache: EngagementTermsCache;
}

export const useEngagementTermsLifecycle = ({
	campaignId,
	marketBlockId,
	creatorAssignmentId,
	onHydrate,
	cache,
}: Options) => {
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [updatedAt, setUpdatedAt] = useState<string | null>(null);
	const [hasMarketTerms, setHasMarketTerms] = useState(false);
	const [loadError, setLoadError] = useState<unknown>(null);
	const [customizedFields, setCustomizedFields] = useState<EngagementTermsCustomizationField[]>([]);
	const marketSnapshotRef = useRef<CetSnapshot>(createEmptySnapshot());
	const marketKeyRef = useRef<string | null>(null);
	const requestKeyRef = useRef<string | null>(null);
	const canPersist = !!campaignId && !!marketBlockId && !marketBlockId.startsWith("temp-");
	const marketKey = canPersist ? getEngagementTermsMarketKey(campaignId, marketBlockId) : null;
	const requestKey = canPersist ? `${campaignId}:${marketBlockId}:${creatorAssignmentId || "market"}` : null;
	const creatorKey =
		canPersist && creatorAssignmentId
			? getEngagementTermsCreatorKey(campaignId, marketBlockId, creatorAssignmentId)
			: null;
	const requestIsCached =
		!!marketKey && cache.marketTerms.has(marketKey) && (!creatorKey || cache.creatorTerms.has(creatorKey));
	const [completedRequestKey, setCompletedRequestKey] = useState<string | null>(null);

	useLayoutEffect(() => {
		let cancelled = false;
		setLoadError(null);

		if (marketKeyRef.current !== marketKey) {
			marketKeyRef.current = marketKey;
			marketSnapshotRef.current = createEmptySnapshot();
			setHasMarketTerms(false);
		}

		if (requestKeyRef.current !== requestKey) {
			requestKeyRef.current = requestKey;
			onHydrate(createEmptySnapshot());
			setUpdatedAt(null);
			setCustomizedFields([]);
		}

		if (!canPersist || !campaignId || !marketBlockId) {
			setLoading(false);
			setCompletedRequestKey(null);
			return () => {
				cancelled = true;
			};
		}

		if (requestIsCached && marketKey) {
			const marketTerms = cache.marketTerms.get(marketKey) || null;
			setHasMarketTerms(!!marketTerms);
			const marketSnapshot = marketTerms ? snapshotFromResponse(marketTerms) : createEmptySnapshot();
			marketSnapshotRef.current = marketSnapshot;
			const creatorTerms = creatorKey ? cache.creatorTerms.get(creatorKey) || null : null;
			const resolvedTerms = creatorTerms || marketTerms;
			onHydrate(resolvedTerms ? snapshotFromResponse(resolvedTerms) : marketSnapshot);
			setCustomizedFields(creatorTerms?.customizedFields || []);
			setUpdatedAt(resolvedTerms?.updatedAt || null);
			setCompletedRequestKey(requestKey);
			setLoading(false);
			return () => {
				cancelled = true;
			};
		}

		setLoading(true);
		void (async () => {
			const marketTerms = await loadCachedMarketTerms(cache, campaignId, marketBlockId);
			if (cancelled || marketKeyRef.current !== marketKey) return;

			setHasMarketTerms(!!marketTerms);
			const marketSnapshot = marketTerms ? snapshotFromResponse(marketTerms) : createEmptySnapshot();
			marketSnapshotRef.current = marketSnapshot;

			if (!creatorAssignmentId) {
				onHydrate(marketSnapshot);
				setCustomizedFields([]);
				setUpdatedAt(marketTerms?.updatedAt || null);
				return;
			}

			const creatorTerms = await loadCachedCreatorTerms(cache, campaignId, marketBlockId, creatorAssignmentId);
			if (cancelled || marketKeyRef.current !== marketKey) return;
			const resolvedTerms = creatorTerms || marketTerms;
			onHydrate(resolvedTerms ? snapshotFromResponse(resolvedTerms) : marketSnapshot);
			setCustomizedFields(creatorTerms?.customizedFields || []);
			setUpdatedAt(resolvedTerms?.updatedAt || null);
		})()
			.catch((error) => {
				if (!cancelled) setLoadError(error);
			})
			.finally(() => {
				if (!cancelled) {
					setCompletedRequestKey(requestKey);
					setLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [
		cache,
		campaignId,
		canPersist,
		creatorAssignmentId,
		creatorKey,
		marketBlockId,
		marketKey,
		onHydrate,
		requestIsCached,
		requestKey,
	]);

	const recordSaved = useCallback(
		(saved: CampaignEngagementTerms): CetSnapshot => {
			if (!campaignId || !marketBlockId) throw new Error("MARKET_MUST_BE_SAVED_FIRST");
			if (creatorAssignmentId) {
				cache.creatorTerms.set(
					getEngagementTermsCreatorKey(campaignId, marketBlockId, creatorAssignmentId),
					saved,
				);
			} else {
				cache.marketTerms.set(getEngagementTermsMarketKey(campaignId, marketBlockId), saved);
				marketSnapshotRef.current = snapshotFromResponse(saved);
				invalidateCachedCreatorTermsForMarket(cache, campaignId, marketBlockId);
			}
			setUpdatedAt(saved.updatedAt);
			setCustomizedFields(saved.customizedFields);
			return snapshotFromResponse(saved);
		},
		[cache, campaignId, creatorAssignmentId, marketBlockId],
	);

	const save = useCallback(
		async (snapshot: CetSnapshot): Promise<CampaignEngagementTerms> => {
			if (!canPersist || !campaignId || !marketBlockId) {
				throw new Error("MARKET_MUST_BE_SAVED_FIRST");
			}
			setSaving(true);
			try {
				const fields = creatorAssignmentId ? getCustomizedFields(snapshot, marketSnapshotRef.current) : [];
				const saved = creatorAssignmentId
					? await engagementTermsAPI.saveCreatorTerms(
							campaignId,
							marketBlockId,
							creatorAssignmentId,
							snapshotToPayload(snapshot, fields),
						)
					: await engagementTermsAPI.saveMarketTerms(campaignId, marketBlockId, snapshotToPayload(snapshot));
				if (creatorAssignmentId) {
					cache.creatorTerms.set(
						getEngagementTermsCreatorKey(campaignId, marketBlockId, creatorAssignmentId),
						saved,
					);
				} else {
					cache.marketTerms.set(getEngagementTermsMarketKey(campaignId, marketBlockId), saved);
					marketSnapshotRef.current = snapshotFromResponse(saved);
					invalidateCachedCreatorTermsForMarket(cache, campaignId, marketBlockId);
					setHasMarketTerms(true);
				}
				setUpdatedAt(saved.updatedAt);
				setCustomizedFields(saved.customizedFields);
				return saved;
			} finally {
				setSaving(false);
			}
		},
		[cache, campaignId, canPersist, creatorAssignmentId, marketBlockId],
	);

	const isLoading = !requestIsCached && (loading || (requestKey !== null && completedRequestKey !== requestKey));
	const previewCustomizedFields = useCallback(
		(snapshot: CetSnapshot): EngagementTermsCustomizationField[] =>
			creatorAssignmentId ? getCustomizedFields(snapshot, marketSnapshotRef.current) : [],
		[creatorAssignmentId],
	);

	return {
		loading: isLoading,
		saving,
		updatedAt,
		hasMarketTerms,
		loadError,
		canPersist,
		customizedFields,
		previewCustomizedFields,
		recordSaved,
		save,
	};
};
